import { c as _c } from "react/compiler-runtime";
import type { JSX } from 'react';
import Script from 'next/script';

/**
 * The two bits of the Cookiebot dialog's Extern skin a stylesheet cannot
 * reach: "Show details" moved into the button group, and the "Do Not Sell or
 * Share" link scoped to visitors Cookiebot places under the CCPA. Copied from
 * extern-website's `components/consent/cookiebot-skin-script.tsx`, itself a
 * copy of the `<script>` www.extern.com's Webflow body carries, so every
 * extern.com surface shows the same banner. Edit all or none. The stylesheet
 * half is `cookiebot-skin.css`. Copy and button labels are dashboard fields,
 * not code.
 *
 * `data-cookieconsent="ignore"`: this draws the consent banner itself, so it
 * has to run when no consent exists yet.
 *
 * Hooks `CookiebotOnDialogDisplay` and not `DOMContentLoaded`: Cookiebot defers
 * `DOMContentLoaded` listeners by several seconds. `CookiebotOnDialogInit` is
 * no alternative either, it fires before the template is built. Everything
 * here is idempotent and re-runnable, because Cookiebot rebuilds the dialog
 * from scratch on renew and on closing the Customize panel, and the event
 * fires again.
 *
 * `afterInteractive` rather than `beforeInteractive`: `uc.js` is
 * `beforeInteractive` and hydration waits on it, so by the time this runs the
 * dialog may already be on the page, which is why the skin is applied once on
 * parse as well as on every display event.
 */
export const COOKIEBOT_DIALOG_DISPLAY_EVENT = 'CookiebotOnDialogDisplay';

/**
 * The three bits of the Cookiebot dialog's Extern skin a stylesheet cannot
 * reach: "Show details" moved into the button group, the title's last word set
 * in serif, and the accept button relabelled. Copied from extern-website's
 * `components/consent/cookiebot-skin-script.tsx`, itself a copy of the
 * `<script>` www.extern.com's Webflow body carries, so every extern.com surface
 * shows the same banner. Edit all or none. The stylesheet half lives in
 * `app/globals.css`.
 *
 * `data-cookieconsent="ignore"`: this draws the consent banner itself, so it
 * has to run when no consent exists yet.
 *
 * Hooks `CookiebotOnDialogDisplay` and not `DOMContentLoaded`: Cookiebot defers
 * `DOMContentLoaded` listeners by several seconds. `CookiebotOnDialogInit` is
 * no alternative either — it fires before the template is built, so the title
 * does not exist yet. Everything here is idempotent and re-runnable, because
 * Cookiebot rebuilds the dialog from scratch on renew and on closing the
 * Customize panel, and the event fires again.
 *
 * `afterInteractive` rather than `beforeInteractive`: `uc.js` is
 * `beforeInteractive` and hydration waits on it, so by the time this runs the
 * dialog may already be on the page — which is why the skin is applied once on
 * parse as well as on every display event.
 */
export function CookiebotSkinScript() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Script data-cookieconsent="ignore" id="extern-cookiebot-skin" strategy="afterInteractive">{`(function () {
  var OUTSIDE_CALIFORNIA_CLASS = 'cb-outside-california';

  // "Show details" (labelled "Manage" in the dashboard) renders inside #...TabContent
  // while the buttons render inside #...Footer -- sibling subtrees under
  // .CybotCookiebotDialogContentWrapper, so flex \`order\` cannot reach across them, and
  // display:contents on the wrappers in between would take the banner's scroll container
  // and the Customize panel down with it. Moving the node drags the tab order along with
  // the visual order; a CSS reorder would have left the two out of step.
  //
  // It goes to #...BodyButtons at every width and stays there -- no restore, so nothing
  // to get stranded. Deliberately not #...BodyButtonsWrapper inside it: that wrapper's
  // children belong to Cookiebot's sortBannerButtons(), which clears it before
  // re-appending the buttons, and a link parked in there is destroyed on the next resize.
  function placeMoreDetails() {
    var moreDetails = document.getElementById('CybotCookiebotDialogBodyEdgeMoreDetails');
    var buttonGroup = document.getElementById('CybotCookiebotDialogBodyButtons');

    if (!moreDetails || !buttonGroup) { return; }
    if (moreDetails.parentElement === buttonGroup) { return; }

    buttonGroup.appendChild(moreDetails);
  }

  // "Do Not Sell or Share My Personal Information" is the link title Cal. Civ. Code
  // 1798.135(a)(1) prescribes, and the dashboard shows it to every visitor. Cookiebot
  // resolves the visitor's regime before it builds the dialog (\`Cookiebot.regulations\`,
  // geo-IP, California for ccpaApplies), so the class goes on when that says the CCPA
  // does not apply and the stylesheet hides the link. Fail open: no regulations object,
  // no class, link stays.
  function scopeDoNotSellLink() {
    var dialog = document.getElementById('CybotCookiebotDialog');
    var regulations = window.Cookiebot && window.Cookiebot.regulations;

    if (!dialog || !regulations) { return; }

    dialog.classList.toggle(OUTSIDE_CALIFORNIA_CLASS, regulations.ccpaApplies !== true);
  }

  function applyExternSkin() {
    // Each in its own try: a throw in one must not skip the other.
    try { placeMoreDetails(); } catch (e) { /* never break the page */ }
    try { scopeDoNotSellLink(); } catch (e) { /* never break the page */ }
  }

  // Switching tabs does not reset either: sortBannerButtons() re-appends the same nodes
  // rather than re-rendering them, and the class sits on the dialog root. Only a full
  // rebuild loses them, and that fires CookiebotOnDialogDisplay again.
  window.addEventListener('${COOKIEBOT_DIALOG_DISPLAY_EVENT}', applyExternSkin);
  // In case the dialog is already on the page by the time this parses.
  applyExternSkin();
})();`}</Script>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}